export const PUBLIC_ORIGIN = 'https://laudando.online';
export const APP_ORIGIN = 'https://app.laudando.online';

const APP_HOST = 'app.laudando.online';
const PUBLIC_HOSTS = new Set(['laudando.online', 'www.laudando.online']);
const ACQUISITION_KEYS = new Set([
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'gclid', 'fbclid', 'acq_surface', 'acq_template_id', 'acq_landing_path',
]);

const APP_PREFIXES = [
  '/painel',
  '/comece',
  '/perfil',
  '/modelos',
  '/assinaturas',
  '/locais',
  '/novo-laudo',
  '/editar-laudo',
  '/meus-laudos',
  '/moderacao',
  '/admin',
  '/assinatura/',
];

const PUBLIC_PREFIXES = [
  '/recursos',
  '/como-funciona',
  '/modelos-laudo',
  '/referencias-laudos',
  '/sistema-de-laudos-medicos',
  '/software-para-laudos-medicos',
  '/programa-para-laudos-medicos',
  '/sobre',
  '/privacidade',
  '/seguranca',
  '/laudo/',
  '/report/',
];

const normalizePath = (path = '/') => {
  const value = String(path || '/');
  return value.startsWith('/') ? value : `/${value}`;
};

const extractTemplateIdFromDestination = (destination = '') => {
  try {
    const parsed = new URL(destination, APP_ORIGIN);
    const direct = parsed.searchParams.get('templateId');
    if (direct) return direct;
    const redirect = parsed.searchParams.get('redirect');
    if (!redirect) return null;
    const nested = new URL(redirect, APP_ORIGIN);
    return nested.searchParams.get('templateId');
  } catch {
    return null;
  }
};

const deriveBrowserAcquisition = (destination = '') => {
  if (typeof window === 'undefined' || !window.location) return null;
  const host = String(window.location.hostname || '').toLowerCase();
  if (!PUBLIC_HOSTS.has(host)) return null;

  const params = Object.fromEntries(new URLSearchParams(window.location.search || ''));
  const pathname = normalizePath(window.location.pathname || '/');
  if (pathname === '/modelos-laudo' || pathname.startsWith('/modelos-laudo/')) {
    params.acq_surface = pathname === '/modelos-laudo' ? 'public_template_catalog' : 'public_template';
    params.acq_landing_path = pathname;
    const templateId = extractTemplateIdFromDestination(destination);
    if (templateId) params.acq_template_id = templateId;
  } else if (pathname === '/referencias-laudos' || pathname.startsWith('/referencias-laudos/')) {
    params.acq_surface = 'seo_reference';
    params.acq_landing_path = pathname;
  } else if (['/sistema-de-laudos-medicos', '/software-para-laudos-medicos', '/programa-para-laudos-medicos'].includes(pathname)) {
    params.acq_surface = 'seo_commercial';
    params.acq_landing_path = pathname;
  }
  return params;
};

const appendAcquisition = (url, acquisition = null) => {
  const source = acquisition || deriveBrowserAcquisition(url);
  if (!source || typeof source !== 'object') return url;
  const parsed = new URL(url);
  for (const [key, rawValue] of Object.entries(source)) {
    if (!ACQUISITION_KEYS.has(key)) continue;
    const value = String(rawValue ?? '').trim();
    if (!value) continue;
    parsed.searchParams.set(key, value.slice(0, 300));
  }
  return parsed.toString();
};

export const appUrl = (path = '/', acquisition = null) =>
  appendAcquisition(`${APP_ORIGIN}${normalizePath(path)}`, acquisition);

export const publicUrl = (path = '/') => `${PUBLIC_ORIGIN}${normalizePath(path)}`;

export const isAppPath = (pathname = '/') => {
  const path = normalizePath(pathname);
  if (path === '/login' || path === '/app' || path.startsWith('/app/')) return true;
  if (path === '/planos') return false;
  return APP_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`) || (prefix.endsWith('/') && path.startsWith(prefix)));
};

export const isPublicPath = (pathname = '/') => {
  const path = normalizePath(pathname);
  if (path === '/' || path === '/planos') return true;
  return PUBLIC_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`) || (prefix.endsWith('/') && path.startsWith(prefix)));
};

export const legacyAppPathToClean = (pathname = '') => {
  const path = String(pathname || '');
  if (path === '/app' || path === '/app/') return '/painel';
  if (path.startsWith('/app/')) return path.slice(4) || '/painel';
  return path || '/painel';
};

export const resolveSiteMode = (hostname = '', pathname = '/') => {
  const host = String(hostname || '').toLowerCase();
  if (host === APP_HOST) return 'app';
  if (PUBLIC_HOSTS.has(host)) return 'public';

  // Localhost e previews precisam conseguir exercitar ambos os fluxos.
  return isAppPath(pathname) ? 'app' : 'public';
};
